/** Display-only comparison. Never rewrites prices or converts currencies. */
export function hasLowerComparablePrice(current: string, previous: string, currency?: string): boolean {
  const a = comparablePrice(current);
  const b = comparablePrice(previous);
  if (a === null || b === null || a.amount >= b.amount) return false;
  const unit = `${currency ?? ''}`.trim().toUpperCase();
  if (!unit) return a.unit === b.unit;
  if (!/^(?:[A-Z]{3}|KR|[$€£¥])$/u.test(unit)) return false;
  const explicitUnits = [a.unit, b.unit].filter(value => value && value !== unit);
  if (explicitUnits.length === 0) return true;
  if (!/^[A-Z]{3}$/.test(unit)) return false;
  // Metadata resolves symbols such as $ or kr without inferring their currency.
  const symbol = new Intl.NumberFormat('en', {style: 'currency', currency: unit, currencyDisplay: 'narrowSymbol'})
    .formatToParts(0).find(part => part.type === 'currency')?.value.toUpperCase();
  return explicitUnits.every(value => value === symbol);
}

function comparablePrice(value: string): {amount: number; unit: string} | null {
  // Only a plain amount with one currency token is comparable. Ranges, qualifiers,
  // conflicting currencies and ambiguous three-digit decimals fail closed.
  const match = value.trim().match(/^(?:(?<prefix>[A-Za-z]{3}|kr|[$€£¥])\s*)?(?<number>\d[\d\s.,]*)(?:\s*(?<suffix>[A-Za-z]{3}|kr|[$€£¥]))?$/u);
  if (!match?.groups || (match.groups['prefix'] && match.groups['suffix'])) return null;
  const unit = (match.groups['prefix'] || match.groups['suffix'] || '').toUpperCase();
  const text = match.groups['number'].trim().replace(/[\u00a0\u202f]/g, ' ');
  let normalized: string;
  if (/^\d+(?:[.,]\d{1,2})?$/.test(text)) {
    normalized = text.replace(',', '.');
  } else if (/^\d{1,3}(?: \d{3})+(?:[.,]\d{1,2})?$/.test(text)) {
    normalized = text.replace(/ /g, '').replace(',', '.');
  } else if (/^\d{1,3}(?:,\d{3})+\.\d{1,2}$/.test(text)) {
    normalized = text.replace(/,/g, '');
  } else if (/^\d{1,3}(?:\.\d{3})+,\d{1,2}$/.test(text)) {
    normalized = text.replace(/\./g, '').replace(',', '.');
  } else {
    return null;
  }
  const amount = Number(normalized);
  return Number.isFinite(amount) && amount <= Number.MAX_SAFE_INTEGER ? {amount, unit} : null;
}
